// EventOverview.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music,
  Users,
  ThumbsDown,
  ThumbsUp,
  DollarSign,
  Zap,
  X,
  Menu,
  Mic,
  Play,
} from "lucide-react";
import MobileNBottomNav from "../components/ui/MobileBottomNav";
import Sidebar from "../components/ui/Sidebar";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";
import { IoArrowBack } from "react-icons/io5";

export default function EventOverview() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarPage, setSidebarPage] = useState("");

  // State for all data
  const [eventData, setEventData] = useState({
    eventDetails: null,
    venueOverview: null,
    earnings: null,
    shoutOut: null,
    queue: null,
  });

  const handleSidebarNav = (page) => {
    setSidebarPage(page);
    setIsSidebarOpen(false);
    switch (page) {
      case "home":
        navigate("/loggedinartist");
        break;
      case "calendar":
        navigate("/bookevent");
        break;
      case "qr":
        navigate("/me");
        break;
      case "profile":
        navigate("/artistprofile");
        break;
      default:
        break;
    }
  };

  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. First fetch event details
        const eventRes = await fetchWithAuth(
          API_ENDPOINTS.EVENT_BY_ID(eventId),
        );
        const eventDetails = await eventRes.json();

        // 2. Then fetch venue overview using venueId from event details
        const venueRes = await fetchWithAuth(
          API_ENDPOINTS.VENUE_OVERVIEW(eventDetails.venueId),
        );
        const venueOverview = await venueRes.json();

        // 3. Fetch earnings data
        const earningsRes = await fetchWithAuth(
          API_ENDPOINTS.TRANSACTIONS_EVENT_EARNINGS(eventId),
        );
        const earningsData = await earningsRes.json();

        // 4. Fetch shout-out data
        const shoutOutRes = await fetchWithAuth(
          API_ENDPOINTS.SHOUT_OUT_EVENT_HISTORY(eventId),
        );
        const shoutOutData = await shoutOutRes.json();

        // 5. Fetch queue data
        const queueRes = await fetchWithAuth(
          API_ENDPOINTS.QUEUE_EVENT_HISTORY(eventId),
        );
        const queueData = await queueRes.json();

        setEventData({
          eventDetails,
          venueOverview,
          earnings: earningsData,
          shoutOut: shoutOutData,
          queue: queueData,
        });
      } catch (err) {
        console.error("⚠️ Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-b from-purple-50 to-white">
        <div className="animate-spin h-12 w-12 border-t-4 border-logo rounded-full" />
      </div>
    );
  }

  if (!eventData.eventDetails || !eventData.venueOverview) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Unable to load event data.
      </div>
    );
  }

  // Destructure data
  const {
    name: eventName,
    themes,
    startAt,
    endAt,
    status,
  } = eventData.eventDetails;

  const { name: venueName, address, photoUrl } = eventData.venueOverview;

  const { totalEarnings, totalCoinsSpent, totalUniqueUsers } =
    eventData.earnings;

  const { totalRequested: shoutOutRequested, totalPlayed: shoutOutPlayed } =
    eventData.shoutOut.statistics;

  const {
    totalRequested: queueRequested,
    totalAccepted,
    totalPlayed,
    totalRejected,
    totalFastTrack,
  } = eventData.queue.statistics;

  // Format date and time
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  // Determine theme text
  const themeText = Array.isArray(themes) ? themes.join(", ") : "—";

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-purple-50 to-white w-screen px-3 py-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <MobileNBottomNav />
      {/* Mobile Sidebar Toggle */}
      <div className="fixed top-0 left-0 z-50 w-full px-2 py-3 flex items-center justify-between bg-logo lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/images/tringbox-logo.png" className="h-6" />
        </div>

        <motion.button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-md"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </motion.button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 w-80 h-full transition-transform duration-300 ease-in-out z-40 ${
          isSidebarOpen ? "translate-x-0" : "lg:translate-x-0 -translate-x-full"
        }`}
      >
        <Sidebar currentPage={sidebarPage} onPageChange={handleSidebarNav} />
      </div>

      {/* Content */}
      <div className="flex-1 min-h-screen lg:pl-96 lg:pr-16 lg:py-8 py-14 flex flex-col w-full">
        {/* Hero Image */}
        <div className="relative rounded-2xl overflow-hidden shadow-lg mb-6">
          <img
            src={photoUrl || "/images/event-placeholder.jpg"}
            alt={venueName}
            className="w-full h-64 object-cover"
          />

          {/* Back Arrow Button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 bg-black/50 text-white p-2 rounded-full shadow-md hover:bg-black/70 transition"
          >
            <IoArrowBack className="h-5 w-5" />
          </button>

          {/* Status Badge */}
          <span
            className={`absolute top-4 right-4 text-white px-3 py-1 rounded-lg text-sm ${
              status === "finished" ? "bg-red-600" : "bg-green-600"
            }`}
          >
            {status === "finished" ? "Ended" : "Active"}
          </span>
        </div>

        {/* Title & Info */}
        <div className="space-y-1 mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900">
            {eventName || "Event"}
          </h2>
          {/* <p className="text-gray-700">{venueName || "Venue"}</p> */}
          <p className="text-gray-600 truncate">
            {address?.formatted_address || "Address not available"}
          </p>
          <p className="text-logo font-medium">Theme: {themeText}</p>
          <div className="flex flex-col">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Started:</span>{" "}
              {formatDate(startAt)}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Ended:</span> {formatDate(endAt)}
            </div>
          </div>
        </div>

        {/* Stats Sections */}
        <div className="space-y-8">
          {/* Revenue Section */}
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Revenue</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <StatCard
                icon={<DollarSign size={24} />}
                label="Total Earnings"
                value={`${totalEarnings || 0}`}
                highlight
              />
              <StatCard
                icon={<DollarSign size={24} />}
                label="Coins Spent"
                value={totalCoinsSpent || 0}
              />
              <StatCard
                icon={<Users size={24} />}
                label="Unique Users"
                value={totalUniqueUsers || 0}
              />
            </div>
          </div>

          {/* Songs Section */}
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Songs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              <StatCard
                icon={<Music size={24} />}
                label="Requested"
                value={queueRequested || 0}
              />
              <StatCard
                icon={<ThumbsUp size={24} />}
                label="Accepted"
                value={totalAccepted || 0}
              />
              <StatCard
                icon={<ThumbsDown size={24} />}
                label="Rejected"
                value={totalRejected || 0}
              />
              <StatCard
                icon={<Zap size={24} />}
                label="Fast Track"
                value={totalFastTrack || 0}
              />
              <StatCard
                icon={<Play size={24} />}
                label="Played"
                value={totalPlayed || 0}
              />
            </div>
          </div>

          {/* Shout Out Section */}
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Shout Outs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <StatCard
                icon={<Mic size={24} />}
                label="Requested"
                value={shoutOutRequested || 0}
              />
              <StatCard
                icon={<Play size={24} />}
                label="Played"
                value={shoutOutPlayed || 0}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/** Stat card component */
function StatCard({ icon, label, value, highlight = false }) {
  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl border-2 border-purple-300 bg-gray-100 shadow-sm`}
    >
      <div className="text-logo">{icon}</div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-600">{label}</div>
      </div>
    </div>
  );
}
